// ============================================================
// 17. ProfitMarginReportPage.tsx
// ============================================================
// src/features/reports/pages/ProfitMarginReportPage.tsx

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { getBranches } from '@/features/settings/services/settingsApi';
import { getProfitMarginReport, getExportUrl } from '../services/reportsApi';
import { ReportHeader } from '../components/ReportHeader';
import { ReportFilters } from '../components/ReportFilters';
import { SummaryCard } from '../components/SummaryCard';
import {
    DollarSign, TrendingUp, TrendingDown, Percent,
    Target, Package, BarChart3, PieChart,
    ArrowUp, ArrowDown, Zap
} from 'lucide-react';
import { motion } from 'framer-motion';

export function ProfitMarginReportPage() {
    const user = useAuthStore((s) => s.user);
    const tenantId = user?.tenantId || '';

    const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
    const [branchId, setBranchId] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [sortBy, setSortBy] = useState<'profit' | 'margin'>('profit');

    const { data: branches } = useQuery({
        queryKey: ['branches-report', tenantId],
        queryFn: () => getBranches(tenantId),
        enabled: !!tenantId,
    });

    const params = {
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        branchId: branchId || undefined,
    };

    const { data, isLoading } = useQuery({
        queryKey: ['report-profit-margin', tenantId, params],
        queryFn: () => getProfitMarginReport(tenantId, params),
        enabled: !!tenantId,
    });

    const formatNaira = (val: string | number) =>
        `₦${parseFloat(String(val || '0')).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
    const formatNumber = (val: string | number) => parseFloat(String(val || '0')).toLocaleString();

    const branchesList = (branches || []) as any[];

    const setQuickDate = (days: number) => {
        const end = new Date();
        const start = new Date();
        if (days === 0) start.setHours(0, 0, 0, 0);
        else start.setDate(start.getDate() - days);
        setDateRange({ startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] });
    };

    const handleExport = (format: 'pdf' | 'excel') => {
        const url = getExportUrl(tenantId, 'profit_margin', format, params as any);
        window.open(url, '_blank');
    };

    const hasFilters = !!(dateRange.startDate || dateRange.endDate || branchId);
    const clearFilters = () => { setDateRange({ startDate: '', endDate: '' }); setBranchId(''); };

    const products = data?.products || [];
    const sortedProducts = [...products].sort((a: any, b: any) => {
        if (sortBy === 'profit') return parseFloat(b.profit) - parseFloat(a.profit);
        return parseFloat(b.margin) - parseFloat(a.margin);
    });

    const highMarginProducts = products.filter((p: any) => parseFloat(p.margin) > 50);
    const lowMarginProducts = products.filter((p: any) => parseFloat(p.margin) < 10);
    const negativeMarginProducts = products.filter((p: any) => parseFloat(p.profit) < 0);

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-green-50/30">
            <ReportHeader
                title="Profit Margin Analysis"
                description="Product profitability breakdown"
                icon={Percent}
                iconGradient="from-green-500 to-emerald-600"
                hasFilters={hasFilters}
                showFilters={showFilters}
                onToggleFilters={() => setShowFilters(!showFilters)}
                onExportPdf={() => handleExport('pdf')}
                onExportExcel={() => handleExport('excel')}
                branchName={branchesList.find(b => b.id === branchId)?.name}
            />

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-5">
                <ReportFilters
                    visible={showFilters}
                    dateRange={dateRange}
                    branchId={branchId}
                    branches={branchesList}
                    onDateRangeChange={setDateRange}
                    onBranchChange={setBranchId}
                    onClear={clearFilters}
                    onQuickDate={setQuickDate}
                />

                {isLoading ? (
                    <div className="flex justify-center py-20"><Spinner size="xl" /></div>
                ) : !data || products.length === 0 ? (
                    <EmptyState
                        icon={<Percent className="w-10 h-10" />}
                        title="No Profit Data"
                        description="No sales data found for profit margin analysis."
                        size="lg"
                    />
                ) : (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                            <SummaryCard
                                label="Total Revenue"
                                value={formatNaira(data.summary?.totalRevenue || 0)}
                                icon={DollarSign}
                                gradient="from-emerald-500 to-teal-600"
                            />
                            <SummaryCard
                                label="Total Cost"
                                value={formatNaira(data.summary?.totalCost || 0)}
                                icon={TrendingDown}
                                gradient="from-slate-500 to-slate-600"
                            />
                            <SummaryCard
                                label="Total Profit"
                                value={formatNaira(data.summary?.totalProfit || 0)}
                                icon={TrendingUp}
                                gradient="from-green-500 to-emerald-600"
                            />
                            <SummaryCard
                                label="Overall Margin"
                                value={`${data.summary?.overallMargin || 0}%`}
                                icon={Target}
                                gradient="from-purple-500 to-violet-600"
                            />
                            <SummaryCard
                                label="High Margin (>50%)"
                                value={String(data.summary?.highMarginProducts || 0)}
                                icon={Zap}
                                gradient="from-amber-500 to-orange-600"
                            />
                        </div>

                        {/* Margin Distribution */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <Card className="border-emerald-200 bg-emerald-50/30">
                                <CardContent className="p-4 text-center">
                                    <ArrowUp className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                                    <p className="text-xs text-slate-500">High Margin Products</p>
                                    <p className="text-2xl font-black text-emerald-700">{highMarginProducts.length}</p>
                                    <p className="text-xs text-slate-400 mt-1">Margin above 50%</p>
                                </CardContent>
                            </Card>
                            <Card className="border-orange-200 bg-orange-50/30">
                                <CardContent className="p-4 text-center">
                                    <ArrowDown className="w-6 h-6 text-orange-600 mx-auto mb-2" />
                                    <p className="text-xs text-slate-500">Low Margin Products</p>
                                    <p className="text-2xl font-black text-orange-700">{lowMarginProducts.length}</p>
                                    <p className="text-xs text-slate-400 mt-1">Margin below 10%</p>
                                </CardContent>
                            </Card>
                            <Card className="border-red-200 bg-red-50/30">
                                <CardContent className="p-4 text-center">
                                    <TrendingDown className="w-6 h-6 text-red-600 mx-auto mb-2" />
                                    <p className="text-xs text-slate-500">Negative Margin</p>
                                    <p className="text-2xl font-black text-red-700">{negativeMarginProducts.length}</p>
                                    <p className="text-xs text-slate-400 mt-1">Selling below cost</p>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Sort Controls */}
                        <div className="flex gap-2">
                            <button
                                onClick={() => setSortBy('profit')}
                                className={`px-3 py-1.5 text-xs font-medium rounded-lg border ${sortBy === 'profit' ? 'bg-green-50 text-green-700 border-green-200' : 'border-slate-200 text-slate-600'
                                    }`}
                            >
                                Sort by Profit
                            </button>
                            <button
                                onClick={() => setSortBy('margin')}
                                className={`px-3 py-1.5 text-xs font-medium rounded-lg border ${sortBy === 'margin' ? 'bg-green-50 text-green-700 border-green-200' : 'border-slate-200 text-slate-600'
                                    }`}
                            >
                                Sort by Margin %
                            </button>
                        </div>

                        <Card className="overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-slate-100">
                                    <thead>
                                        <tr className="bg-slate-50/50">
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Product</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Qty Sold</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Revenue</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Cost</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Profit</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Margin</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Per Unit Profit</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {sortedProducts.map((product: any, i: number) => {
                                            const perUnitProfit = product.quantitySold > 0
                                                ? parseFloat(product.profit) / product.quantitySold
                                                : 0;
                                            const marginNum = parseFloat(product.margin);

                                            return (
                                                <tr key={i} className={`hover:bg-slate-50 transition-colors ${marginNum < 0 ? 'bg-red-50/20' : ''
                                                    }`}>
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-3">
                                                            <div className={`p-2 rounded-lg ${marginNum > 50 ? 'bg-emerald-100' :
                                                                    marginNum > 20 ? 'bg-blue-100' :
                                                                        marginNum > 0 ? 'bg-orange-100' :
                                                                            'bg-red-100'
                                                                }`}>
                                                                {marginNum > 50 ? (
                                                                    <TrendingUp className="w-4 h-4 text-emerald-600" />
                                                                ) : marginNum > 0 ? (
                                                                    <TrendingUp className="w-4 h-4 text-orange-600" />
                                                                ) : (
                                                                    <TrendingDown className="w-4 h-4 text-red-600" />
                                                                )}
                                                            </div>
                                                            <span className="text-sm font-medium text-slate-900">{product.name}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-sm">
                                                        {formatNumber(product.quantitySold)}
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-sm font-bold text-slate-900">
                                                        {formatNaira(product.revenue)}
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-sm text-slate-500">
                                                        {formatNaira(product.cost)}
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-sm font-bold">
                                                        <span className={parseFloat(product.profit) >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                                                            {formatNaira(product.profit)}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <Badge
                                                            variant={marginNum > 50 ? 'success' : marginNum > 20 ? 'info' : marginNum > 0 ? 'warning' : 'danger'}
                                                            size="sm"
                                                            glow={marginNum > 50}
                                                        >
                                                            {product.margin}%
                                                        </Badge>
                                                    </td>
                                                    <td className="px-6 py-4 text-center text-sm">
                                                        <span className={perUnitProfit >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                                                            {formatNaira(perUnitProfit.toFixed(2))}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                    </>
                )}
            </div>
        </div>
    );
}